import type { Dispatch, SetStateAction } from 'react'
import React, { useContext as useContextCore } from 'react'
import { useLocalStorage } from 'react-use'
import type { UseListResult } from '@ztechapps/common.hook'
import { StorageType, useList } from '@ztechapps/common.hook'
import { GridListViewModeEnum } from '@ztechapps/webui.data-tools'
import { queries } from './graphql'

const BLOG_LIST_VIEW_MODE_STORAGE_KEY = 'blog-list-view-mode'

// `list` stays loosely typed (UseListResult's default `T = any`) — `queries.allConfig`
// (buildGraphqlQuery) does not infer full `Blog` shapes, and Layout.tsx spreads each row
// straight into `BlogBox`'s required props, so narrowing to `Blog` here breaks that spread.
export type BlogListContextType = UseListResult & {
  filterCategory: string
  viewMode: GridListViewModeEnum
  setViewMode: Dispatch<SetStateAction<GridListViewModeEnum | undefined>>
}

const context = React.createContext<BlogListContextType>({} as BlogListContextType)
const CoreProvider = context.Provider

export type BlogListProviderProps = {
  children: React.ReactNode
}

export const BlogListProvider = ({ children, ...props }: BlogListProviderProps) => {
  const filterCategory = 'filter_blogs'

  // List is the default so a duplicate slug is visible on first load — the Grid view's
  // `BlogBox` cards render no slug at all.
  const [viewMode, setViewMode] = useLocalStorage<GridListViewModeEnum>(
    BLOG_LIST_VIEW_MODE_STORAGE_KEY,
    GridListViewModeEnum.List
  )

  const listResult = useList({
    ...queries.allConfig,
    sort: { updatedAt: -1 },
    subscribeToUpdates: true,
    listActionOptions: {
      storageKey: filterCategory,
      storageType: StorageType.sessionStorage,
    },
  })

  return (
    <CoreProvider
      value={{ ...props, ...listResult, filterCategory, viewMode: viewMode ?? GridListViewModeEnum.List, setViewMode }}
    >
      {children}
    </CoreProvider>
  )
}

export const useBlogListContext = () => useContextCore(context)
